import React, { FC, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ThemeName } from "@evergis/ui";

import { BackToLayers } from "../../../components/Layout/styled";
import { PreloaderText, ProjectTitle } from "../../ProjectPanel/styled";
import {
  PresentationHeader,
  PresentationHeaderButtons,
  PresentationHeaderIcon,
  PresentationHeaderTools,
  PresentationPanelContainer,
  PresentationPanelWrapper,
} from "../styled";
import { PresentationHeaderImage } from "../../FeatureCard/styled";
import { Dashboard } from "./Dashboard";
import { Placeholder } from "../../../components/Placeholder/Placeholder";
import { LayerListModule as LayerList } from "../../LayerList/LayerList";
import { ThemeSwitcher } from "../../ProjectPanel/components/ThemeSwitcher";
import { GeometryFilterSwitcher } from "../../ProjectPanel/components/GeometryFilterSwitcher";
import { ProjectInfoButton } from "../../ProjectPanel/ProjectInfoButton";

import { getThemeName } from "../../../ducks/theme/selectors";
import { useProjectPanel } from "../../ProjectPanel/hooks/useProjectPanel";
import { useToggle } from "../../../hooks/useToggle";
import { useConfigPages } from "../hooks/useConfigPages";
import { usePageConfig } from "../hooks/usePageConfig";

import { WidgetType } from "../../FeatureCard/types";

export const Presentation: FC<{ left?: number }> = ({ left }) => {
  const { t } = useTranslation("projectPanel");
  const themeName = useSelector(getThemeName);
  const { title } = useProjectPanel();
  const { getPageTitle, getPageImage, getPageIcon } = usePageConfig(WidgetType.Dashboard);
  const { currentPageIndex, currentPage } = useConfigPages(WidgetType.Dashboard);

  const { expandedLayers, iconUrl } = currentPage?.options || {};

  const [showLayers, toggleLayers] = useToggle(expandedLayers);

  const pageTitle = getPageTitle(currentPageIndex);
  const pageImage = getPageImage(currentPageIndex);
  const pageIcon = getPageIcon(currentPageIndex);

  const renderIcon = useMemo(() => {
    if (!pageIcon) return null;

    return (
      <PresentationHeaderIcon
        src={pageIcon}
        alt={pageTitle}
        onClick={iconUrl ? () => window.open(iconUrl, "_blank") : undefined}
      />
    );
  }, [iconUrl, pageIcon, pageTitle]);

  useEffect(() => {
    if ((!currentPage?.children?.length && expandedLayers !== false && !showLayers) || expandedLayers !== showLayers) {
      toggleLayers();
    }
  }, [currentPageIndex, expandedLayers]);

  return (
    <PresentationPanelContainer left={left} showLayers={showLayers}>
      <PresentationPanelWrapper>
        <PresentationHeader hasImage={!!pageImage || !!pageIcon}>
          {renderIcon}
          <ProjectInfoButton style={{ position: "absolute", top: "1rem", right: "0.75rem" }} />
          <ProjectTitle>
            <Placeholder>{pageTitle}</Placeholder>
          </ProjectTitle>
          <PreloaderText>{title}</PreloaderText>
          <PresentationHeaderTools>
            <BackToLayers toggled={showLayers} onClick={toggleLayers}>
              {t(showLayers ? "hideLayers" : "availableLayers")}
            </BackToLayers>
            <PresentationHeaderButtons>
              <GeometryFilterSwitcher />
              <ThemeSwitcher />
            </PresentationHeaderButtons>
          </PresentationHeaderTools>
          {!!pageImage && <PresentationHeaderImage image={pageImage} isDark={themeName === ThemeName.Dark} />}
        </PresentationHeader>
        {showLayers && <LayerList noSearch noMenu noGroups filterByConfig />}
      </PresentationPanelWrapper>
      <Dashboard name="dashboard" modulePath="Dashboard/Dashboard" />
    </PresentationPanelContainer>
  );
};
